import React, { useMemo, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { Sheet } from "@/pages/Familia";
import { useData } from "@/context/DataContext";
import { CONTRATOS_KEY } from "@/mock/data";
import { sugerirAsignacion, esDocHogar } from "@/lib/todosDocs";
import { formatApiErrorDetail } from "@/lib/api";
import { PersonaChip } from "@/components/personas/Avatar";
import { ContactoDesdeDocumento } from "@/components/documentos/ContactoDetectado";

const TIPOS = [
  { id: "vivienda", label: "Casa", icon: "Home" },
  { id: "vehiculo", label: "Coche", icon: "Car" },
  { id: "persona", label: "Familia", icon: "User" },
  { id: "mascota", label: "Mascota", icon: "PawPrint" },
  { id: "contacto", label: "Contacto", icon: "Contact" },
];
const TEMAS = [
  { id: CONTRATOS_KEY, label: "Contratos y servicios personales" },
  { id: "Otros", label: "Otros asuntos" },
];
const NINGUNO = { vivienda: "ninguna casa", vehiculo: "ningún coche", persona: "ninguna persona", mascota: "ninguna mascota" };

const Opcion = ({ activo, onClick, children, testid }) => (
  <button data-testid={testid} onClick={onClick}
    className={`w-full min-h-[52px] px-4 rounded-xl border text-left font-semibold text-base flex items-center gap-2 transition-colors ${activo ? "bg-[#E8F0EC] border-[#3A6B56] text-[#2E5645]" : "bg-white border-[#E5DFD5] text-[#1C2421]"}`}>
    {children}
  </button>
);

export const AsignarSheet = ({ doc, onClose, actual }) => {
  const data = useData();
  const { viviendas, vehiculos, pets, familyMembers, vincularDocumentoVivienda, vincularDocumentoVehiculo, vincularDocumentoMascota, vincularDocumentoPersona } = data;
  const sug = useMemo(() => sugerirAsignacion(doc, data), [doc, data]);
  const [manual, setManual] = useState(!sug || !!actual);
  const vivDoc = !sug && !actual && esDocHogar(doc) && (viviendas || []).length > 0;
  const [tipo, setTipo] = useState(vivDoc ? "vivienda" : "");
  const [ent, setEnt] = useState("");
  const [tema, setTema] = useState("");
  const [busy, setBusy] = useState(false);
  const listas = {
    vivienda: (viviendas || []).map((v) => ({ id: v.id, label: v.alias })),
    vehiculo: (vehiculos || []).map((v) => ({ id: v.id, label: v.alias })),
    persona: (familyMembers || []).map((f) => ({ id: f.id, label: f.name, persona: f })),
    mascota: (pets || []).map((p) => ({ id: p.id, label: p.name })),
  };

  const asignar = async (t, id, tm) => {
    setBusy(true);
    try {
      if (t === "vivienda") await vincularDocumentoVivienda(doc.id, id);
      else if (t === "vehiculo") await vincularDocumentoVehiculo(doc.id, id);
      else if (t === "mascota") await vincularDocumentoMascota(doc.id, id);
      else await vincularDocumentoPersona(doc.id, id, tm);
      toast.success(actual ? "Documento movido" : "Documento clasificado", { description: doc.filename });
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo asignar.");
      setBusy(false);
    }
  };
  const listo = tipo && ent && (tipo !== "persona" || tema);
  const temaSug = TEMAS.find((t) => t.id === sug?.tema)?.label;

  return (
    <Sheet onClose={onClose} testid="asignar-sheet">
      <h2 className="font-heading text-xl font-bold text-[#1C2421] pr-12">{actual ? "¿Dónde lo movemos?" : "¿De qué es este documento?"}</h2>
      <p className="text-sm text-[#6E7A74] mt-1 truncate">{doc.filename}</p>
      {actual && <p data-testid="asignar-actual" className="text-base text-[#1C2421] mt-3 bg-[#F2EDE4] rounded-xl px-4 py-3">Ahora está en: <strong>{actual}</strong></p>}

      {sug && !manual && (
        <div className="mt-5 bg-[#E8F0EC] border border-[#3A6B56]/30 rounded-2xl p-4 space-y-3" data-testid="asignar-sugerencia">
          <p className="text-base text-[#1C2421]">Parece de <strong data-testid="asignar-sugerencia-label">{sug.label}</strong>{temaSug ? ` · ${temaSug}` : ""}.</p>
          <div className="flex gap-2.5">
            <button data-testid="asignar-elegir-otro" onClick={() => setManual(true)} className="flex-1 min-h-[52px] rounded-xl bg-white font-semibold text-base border border-[#E5DFD5]">Elegir otro</button>
            <button data-testid="asignar-confirmar-sugerencia" disabled={busy} onClick={() => asignar(sug.tipo, sug.id, sug.tema)}
              className="flex-1 min-h-[52px] rounded-xl bg-[#3A6B56] text-white font-semibold text-base disabled:opacity-60">{busy ? "Guardando…" : "Confirmar"}</button>
          </div>
        </div>
      )}

      {vivDoc && tipo === "vivienda" && (
        <p className="text-base text-[#1C2421] mt-3 bg-[#EFF6F1] rounded-xl px-4 py-3" data-testid="asignar-viv-pregunta">
          Este documento parece estar relacionado con una vivienda. ¿Quieres asignarlo?
        </p>
      )}
      {manual && (
        <div className="mt-5 space-y-5">
          <div className="grid grid-cols-2 gap-2.5">
            {TIPOS.map((t) => {
              const I = Icons[t.icon];
              return <Opcion key={t.id} testid={`asignar-tipo-${t.id}`} activo={tipo === t.id} onClick={() => { setTipo(t.id); setEnt(""); setTema(""); }}><I size={20} /> {t.label}</Opcion>;
            })}
          </div>
          {tipo === "contacto" && <ContactoDesdeDocumento doc={doc} onCancelar={() => setTipo("")} onTerminar={onClose} />}
          {tipo && tipo !== "contacto" && (listas[tipo].length
            ? <div className="space-y-2" data-testid="asignar-entidades">
                <p className="text-sm font-semibold text-[#4A5550]">Elige cuál</p>
                {listas[tipo].map((x) => <Opcion key={x.id} testid={`asignar-entidad-${x.id}`} activo={ent === x.id} onClick={() => setEnt(x.id)}>{x.persona ? <PersonaChip persona={x.persona} size="sm" /> : x.label}</Opcion>)}
              </div>
            : <p className="text-base text-[#6E7A74]" data-testid="asignar-sin-entidades">Aún no tienes {NINGUNO[tipo]} en AL DÍA. Añádelo primero desde Áreas.</p>)}
          {tipo === "persona" && ent && (
            <div className="space-y-2" data-testid="asignar-temas">
              <p className="text-sm font-semibold text-[#4A5550]">¿De qué tema?</p>
              {TEMAS.map((t) => <Opcion key={t.id} testid={`asignar-tema-${t.id === "Otros" ? "familia" : "contratos"}`} activo={tema === t.id} onClick={() => setTema(t.id)}>{t.label}</Opcion>)}
            </div>
          )}
          {tipo !== "contacto" && <button data-testid="asignar-confirmar" disabled={!listo || busy} onClick={() => asignar(tipo, ent, tema)}
            className="w-full min-h-[52px] rounded-xl bg-[#3A6B56] text-white font-semibold text-base disabled:opacity-40">{busy ? "Guardando…" : "Confirmar"}</button>}
        </div>
      )}
    </Sheet>
  );
};
